import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const emptyForm = {
  invoiceId: "",
  vehicleNo: "",
  transporter: "",
};

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  tableWrap: {
    maxHeight: 390,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function EWayBillPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const getValue = (row, pascal, camel) => row?.[pascal] ?? row?.[camel] ?? "";

  const load = async () => {
    try {
      setLoading(true);
      const res = await client.get("/ewaybill");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      alert(err.userMessage || "Failed to load E-Way Bills");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${getValue(r, "EWayBillNo", "eWayBillNo")} ${getValue(r, "InvoiceId", "invoiceId")} ${getValue(r, "VehicleNo", "vehicleNo")} ${getValue(r, "Transporter", "transporter")}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const save = async (e) => {
    e.preventDefault();

    if (!form.invoiceId || Number(form.invoiceId) <= 0) {
      return alert("Invoice Id is required.");
    }

    try {
      setSaving(true);
      const payload = {
        invoiceId: Number(form.invoiceId),
        vehicleNo: form.vehicleNo?.trim() || null,
        transporter: form.transporter?.trim() || null,
      };
      const res = await client.post("/ewaybill/generate", payload);
      alert(res.data?.message || `E-Way Bill generated: ${res.data?.ewayBillNo || ""}`);
      setForm(emptyForm);
      load();
    } catch (err) {
      alert(err.userMessage || "Failed to generate E-Way Bill");
    } finally {
      setSaving(false);
    }
  };

  const exportJson = (row) => {
    const data = {
      invoiceId: getValue(row, "InvoiceId", "invoiceId"),
      eWayBillNo: getValue(row, "EWayBillNo", "eWayBillNo"),
      vehicleNo: getValue(row, "VehicleNo", "vehicleNo"),
      transporter: getValue(row, "Transporter", "transporter"),
      generatedOn: getValue(row, "GeneratedOn", "generatedOn"),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eway-bill-${data.eWayBillNo || data.invoiceId || "draft"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.page}>
      <div className="row g-2">
        <div className="col-lg-4">
          <div style={styles.card}>
            <div style={styles.header}>
              <div>
                <h6 style={styles.title}>E-Way Bill</h6>
                <div style={styles.sub}>Generate transport-ready E-Way Bill draft data</div>
              </div>
              <i className="bi bi-truck fs-5"></i>
            </div>

            <form onSubmit={save} style={styles.body}>
              <div className="mb-2">
                <label className="form-label mb-1 fw-semibold">Invoice Id</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  style={styles.input}
                  value={form.invoiceId}
                  onChange={(e) => setForm({ ...form, invoiceId: e.target.value })}
                  placeholder="Enter Invoice Id"
                />
              </div>

              <div className="mb-2">
                <label className="form-label mb-1 fw-semibold">Vehicle No</label>
                <input
                  className="form-control text-uppercase"
                  style={styles.input}
                  value={form.vehicleNo}
                  onChange={(e) => setForm({ ...form, vehicleNo: e.target.value.toUpperCase() })}
                  placeholder="AP39AB1234"
                />
              </div>

              <div className="mb-2">
                <label className="form-label mb-1 fw-semibold">Transporter</label>
                <input
                  className="form-control"
                  style={styles.input}
                  value={form.transporter}
                  onChange={(e) => setForm({ ...form, transporter: e.target.value })}
                  placeholder="Transporter name"
                />
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" style={styles.btn} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-plus-circle me-1" />}
                  Generate
                </button>
                <button type="button" className="btn btn-outline-secondary" style={styles.btn} onClick={() => setForm(emptyForm)}>
                  Clear
                </button>
              </div>

              <div className="alert alert-info mt-3 mb-0 py-2" style={{ fontSize: 10 }}>
                This module creates an internal E-Way Bill draft/JSON. For live government portal generation, connect your GST Suvidha Provider / E-Way Bill API credentials in Enterprise setup.
              </div>
            </form>
          </div>
        </div>

        <div className="col-lg-8">
          <div style={styles.card}>
            <div style={styles.header}>
              <div>
                <h6 style={styles.title}>Generated E-Way Bills</h6>
                <div style={styles.sub}>{filteredRows.length} records</div>
              </div>
              <div className="d-flex gap-2">
                <input
                  className="form-control form-control-sm"
                  style={{ ...styles.input, width: 220 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search EWB / invoice / vehicle"
                />
                <button className="btn btn-light btn-sm" style={styles.btn} onClick={load}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </div>
            </div>

            <div style={styles.body}>
              <div style={styles.tableWrap}>
                <table className="table table-hover align-middle" style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>E-Way Bill No</th>
                      <th style={styles.th}>Invoice Id</th>
                      <th style={styles.th}>Vehicle No</th>
                      <th style={styles.th}>Transporter</th>
                      <th style={styles.th}>Generated On</th>
                      <th style={styles.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="7" className="text-center py-4">Loading...</td></tr>
                    ) : filteredRows.length ? (
                      filteredRows.map((r, i) => (
                        <tr key={getValue(r, "Id", "id") || i}>
                          <td style={styles.td}>{i + 1}</td>
                          <td style={styles.td} className="fw-semibold text-primary">{getValue(r, "EWayBillNo", "eWayBillNo")}</td>
                          <td style={styles.td}>{getValue(r, "InvoiceId", "invoiceId")}</td>
                          <td style={styles.td}>{getValue(r, "VehicleNo", "vehicleNo") || "-"}</td>
                          <td style={styles.td}>{getValue(r, "Transporter", "transporter") || "-"}</td>
                          <td style={styles.td}>{getValue(r, "GeneratedOn", "generatedOn") || "-"}</td>
                          <td style={styles.td}>
                            <button className="btn btn-outline-primary btn-sm" style={styles.btn} onClick={() => exportJson(r)}>
                              <i className="bi bi-download me-1"></i>JSON
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="7" className="text-center text-muted py-4">No E-Way Bill records found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
